import { isNolAdmission, isNolAncillary, nolPassProduct, nolVariantName } from "../../Lib/Utils/nolCard";
import { getAllOfferWithoutExchange } from "../../Lib/Utils/booking";
import { calculatePrice } from "../../Lib/Utils/validationUtil";
import {
  AdmissionStatusEnum,
  AncillaryStatusEnum,
  PtModeEnum,
} from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBooking, ILegTrain } from "../../Orchestrator/Types/Index";
import { INolAdded, INolProduct } from "../../Orchestrator/Types/Interface/INol";

/**
 * Statuses that mean "the passenger has PAID for this pass".
 *
 * EXCHANGE_ONGOING is included for the same reason extractCarriedOverAddons
 * includes it: a paid pass must not vanish mid change-date and be re-offered
 * for sale.
 *
 * CONFIRMED is deliberately EXCLUDED. A Nol ancillary added to a settled
 * booking lands CONFIRMED and stays CONFIRMED until payment — a paid one reads
 * FULFILLED (observed on INT 2026-07-26). Counting CONFIRMED as owned told the
 * passenger, on their ticket, that they held a pass they had abandoned mid-
 * payment. The pre-refactor chip filtered FULFILLED only, for exactly this
 * reason.
 */
const PAID_STATUSES = new Set<string>([
  AncillaryStatusEnum.EXCHANGE_ONGOING,
  AncillaryStatusEnum.FULFILLED,
]);

/**
 * Adds the not-yet-paid statuses — attached to the booking but not necessarily
 * paid. This is the DEFAULT, because ownership callers (hydration, the
 * purchasable-vs-owned decision) need to know the pass exists so they don't
 * offer a second one. Display callers that assert the passenger HAS a pass
 * should pass `{ paidOnly: true }`.
 *
 * The two routes land an unpaid pass in DIFFERENT statuses: an ancillary added
 * to a settled booking reads CONFIRMED, while a pass added through
 * add-fmlm-travel-passes is an admission and reads PREBOOKED (INT booking
 * e46ff673, verified on the wire). Without PREBOOKED here the pass a passenger
 * had just added was invisible to every ownership caller — reopening the Nol
 * modal showed nothing selected, and it offered to sell them a second one.
 */
const ATTACHED_STATUSES = new Set<string>([
  ...PAID_STATUSES,
  AncillaryStatusEnum.CONFIRMED,
]);

/**
 * The admission equivalent, which additionally counts PREBOOKED.
 *
 * PREBOOKED means opposite things on the two routes. An unpaid ANCILLARY at
 * PREBOOKED is the leftover of an abandoned session and must be ignored (it
 * reads CONFIRMED once genuinely attached). A pass added through
 * add-fmlm-travel-passes is an ADMISSION and sits at PREBOOKED for the whole
 * of checkout — the same status its train ticket holds — so ignoring it there
 * hid the pass the passenger had just added: the Nol modal reopened with
 * nothing selected and offered to sell them a second one.
 */
const ATTACHED_ADMISSION_STATUSES = new Set<string>([
  ...ATTACHED_STATUSES,
  AncillaryStatusEnum.PREBOOKED,
]);

/**
 * The one status meaning "the passenger HAD this pass and no longer does,
 * with money moved back".
 *
 * REFUNDED only. RELEASED (an expired hold) and EXCHANGED (the pass moved onto
 * a replacement offer) are also terminal, but neither is a cancellation the
 * passenger asked for and neither has refund history to show — listing them
 * under "Cancelled" would invent events. See [[cancelledNolForLeg]].
 */
const CANCELLED_STATUSES = new Set<string>([AncillaryStatusEnum.REFUNDED]);

/**
 * `controlNumber` of the fulfillment issued for one admission, if it has one.
 *
 * Matches on `bookingParts`, the way TicketsModal resolves the TRAIN's own
 * number, and falls back to `fulfillment.id === admission.id` — the identity the
 * refund path already relies on for exactly these passes (`onwardRefund`,
 * `cdtNolRefundRows`, `refundableFulfillments`). Both held on the booking this
 * was built from; accepting either keeps the Nol row joining the way the rest of
 * the Nol code joins.
 */
const fulfillmentNumberFor = (booking: IBooking, admissionId?: string): string => {
  if (!admissionId) return "";
  const fulfillment = (booking?.fulfillments || []).find(
    (f) =>
      f?.id === admissionId ||
      (f?.bookingParts || []).some((part) => part?.id === admissionId)
  );
  return fulfillment?.controlNumber || "";
};

/**
 * The shared scan behind [[bookedNolForLeg]] and [[cancelledNolForLeg]].
 *
 * The two differ ONLY in which statuses count and which offers are looked at,
 * so they share one traversal: a rule that lands in one view but not the other
 * is the class of bug this whole module exists to prevent.
 */
function scanNolForLeg(
  booking: IBooking,
  legIds: Set<string>,
  offers: any[],
  ancillaryStatuses: Set<string>,
  admissionStatuses: Set<string>,
  /**
   * BOOKING passenger id to narrow to (not an externalRef — the wire field is
   * what is filtered, before the externalRef translation below). Omit for every
   * pass on the leg, which is what every pre-existing caller wants.
   *
   * Added for the per-passenger ticket row: a ticket card belongs to ONE
   * passenger, and the leg-wide answer names a variant somebody else may hold.
   * Scoping here rather than at the call site keeps the leg and status rules —
   * the whole reason this module exists — in one traversal.
   */
  passengerId?: string
): (INolAdded & { variant: string }) | null {
  const activeStatuses = ancillaryStatuses;
  const activeAdmissionStatuses = admissionStatuses;

  // A booked ancillary/admission names its riders by the booking passenger's
  // `id`, while every UI surface keys on `externalRef` — the checklist rows, the
  // offer's `offerIdByPassengerRef`, and the ref-set comparisons in
  // onwardConfirmController. Translate once here so `passengerRefs` means the
  // same thing whichever way a pass reached the booking; a value that is already
  // an externalRef (or unknown) passes through untouched.
  const externalRefById = new Map(
    (booking.passengers || []).map((p) => [p.id, p.externalRef])
  );
  const asExternalRef = (passengerId: string): string =>
    externalRefById.get(passengerId) || passengerId;

  const passengerRefs: string[] = [];
  let total = 0;
  let productCode = "";
  let variant = "";
  let ticketNumber = "";

  for (const offer of offers) {
    for (const anc of ((offer as any)?.ancillaries || [])) {
      if (!isNolAncillary((offer as any)?.products, anc)) continue;
      if (!activeStatuses.has(anc?.status as string)) continue;
      if (!(anc?.tripCoverage?.coveredLegIds || []).some((id: string) => legIds.has(id)))
        continue;
      if (passengerId && !(anc?.passengerIds || []).includes(passengerId)) continue;

      // Under a scope, report the rider that MATCHED — not `passengerIds[0]`,
      // which on a multi-rider ancillary names somebody else entirely.
      passengerRefs.push(asExternalRef(passengerId || anc?.passengerIds?.[0] || ""));
      // KNOWN CEILING: `price` on a multi-rider ancillary is the whole party's,
      // so a scoped `total` overstates. No caller reads money under a scope
      // today (TicketNolPass takes only the variant); divide by the rider count
      // here the moment one does.
      total += calculatePrice(anc?.price) || 0;
      if (!productCode) {
        const productId = anc?.products?.[0]?.productId;
        const product = ((offer as any)?.products || []).find((p: any) => p.id === productId);
        productCode = product?.code || anc?.type || "";
        variant = nolVariantName(product?.description);
      }
    }
  }

  // Passes bought through add-fmlm-travel-passes are ADMISSIONS carrying
  // `isFmLmTravelPass`, on their own booked offer — not ancillaries, so the loop
  // above cannot see them. Both shapes are live on real bookings (the ancillary
  // route predates the FMLM endpoint), so scan for both and let the counts add.
  for (const offer of offers) {
    for (const adm of ((offer as any)?.admissions || [])) {
      if (!isNolAdmission(adm)) continue;
      if (!activeAdmissionStatuses.has(adm?.status as string)) continue;
      if (!(adm?.tripCoverage?.coveredLegIds || []).some((id: string) => legIds.has(id)))
        continue;
      if (passengerId && !(adm?.passengerIds || []).includes(passengerId)) continue;

      // One admission per passenger, so each contributes one rider — except
      // under a scope, where only the matched rider counts. Without this a
      // scoped read still returned the whole party in `passengerRefs` and their
      // `count`, contradicting the filter directly above it.
      const riders: string[] = passengerId
        ? [passengerId]
        : (adm?.passengerIds?.length ? adm.passengerIds : [""]);
      passengerRefs.push(...riders.map(asExternalRef));
      total += calculatePrice(adm?.price) || 0;
      if (!productCode) {
        const product = nolPassProduct((offer as any)?.products);
        productCode = product?.code || "";
        // "Gold" / "Regular" — the variant name the chip reads.
        variant = nolVariantName(product?.description);
        // The pass carries no ticket number of its own: it gets its OWN
        // fulfillment, whose `bookingParts` name this admission, and the number
        // is that fulfillment's `controlNumber` — the same field and the same
        // lookup the train ticket prints (TicketsModal
        // `getCurrentPassengerDetails`).
        //
        // CLAIMED BY THE SAME GUARD AS THE NAME, deliberately: the row shows one
        // pass, so its name and its identifier must come off the SAME record. A
        // guard of its own went on scanning until some admission yielded a
        // number, which prints one pass's name over another's identifier the
        // moment a rider holds two (the ancillary and admission routes are both
        // live — see the loop above — and the widened `legIds` can match twice).
        // Failing to an empty number is the safe half: a row with a name and no
        // number is a shape the callers already handle.
        //
        // SCOPED READS ONLY, unlike `total` above, which documents the opposite
        // ceiling. A ticket number belongs to ONE rider's pass, so a leg-wide
        // answer would hand an arbitrary rider's number to a caller asking about
        // the party — a wrong identifier, not merely an imprecise one.
        if (passengerId) ticketNumber = fulfillmentNumberFor(booking, adm?.id);
      }
    }
  }

  if (passengerRefs.length === 0) return null;
  return { productCode, passengerRefs, count: passengerRefs.length, total, variant, ticketNumber };
}

/**
 * Derive the Nol pass booked against `legId`, straight from the booking.
 * There is no Nol hydration endpoint — the booking is the source of truth.
 *
 * DOUBLE-COUNTING, now that callers may pass SEVERAL leg ids (`nolLegIdsFor`).
 * Two passes could match one card: an exchange's auto-linked twin, or a
 * surviving old-leg pass beside a newly added one. Neither fires today —
 * `ATTACHED_STATUSES` excludes PREBOOKED so the twin is filtered, and the
 * second rests on `/fmlm-offers` not offering a pass to someone who owns one
 * (backend behaviour, unguaranteed here). `{ paidOnly: true }` callers are
 * immune either way: PAID_STATUSES excludes PREBOOKED.
 *
 * If it ever does fire, dedupe on `(passengerIds[0], type)` — the twin identity
 * `upgradeAncillaryTwins.buildCarriedOverAncillaryKeys` uses. NOT on `id`: a
 * twin is a distinct record with its own id, so an id-keyed set misses the real
 * duplicate while collapsing legitimately distinct per-rider admissions. (That
 * was tried and reverted; it broke NolCardModal's own suite.)
 *
 * `getAllOfferWithoutExchange` is not blanket protection here — it drops only
 * EXCHANGED offers, so a PENDING exchange's source offer is kept.
 */
export function bookedNolForLeg(
  booking: IBooking | undefined,
  legId: string | string[],
  opts: { paidOnly?: boolean; passengerId?: string } = {}
): (INolAdded & { variant: string }) | null {
  // A LIST is accepted for the same reason `cancelledNolForLeg` takes one: a
  // change-date issues a new leg id while the pass goes on naming the old one,
  // so a checkout asking only about the leg on screen finds nothing. Callers
  // that know about the exchange pass `nolLegIdsFor`; a plain string behaves
  // exactly as before.
  const legIds = (Array.isArray(legId) ? legId : [legId]).filter(Boolean);
  if (!booking || legIds.length === 0) return null;
  return scanNolForLeg(
    booking,
    new Set(legIds),
    getAllOfferWithoutExchange(booking.bookedOffers || []),
    opts.paidOnly ? PAID_STATUSES : ATTACHED_STATUSES,
    opts.paidOnly ? PAID_STATUSES : ATTACHED_ADMISSION_STATUSES,
    opts.passengerId
  );
}

/**
 * The pass this leg HAD and no longer has — the history half of
 * [[bookedNolForLeg]], and the whole of ACPT-012.
 *
 * A change-date auto-cancels the moving leg's Nol (sessionRefundLegs.ts) and
 * flips it to REFUNDED. Every existing reader filters on active statuses, so
 * the pass vanished from Ticket Summary entirely rather than staying on as a
 * cancelled line with its refund beside it.
 *
 * `getAllOfferWithoutExchange` is deliberately NOT applied. That filter drops
 * any offer holding an EXCHANGED admission, which after a committed change-date
 * is the ORIGINAL train offer — and on the ancillary route the cancelled Nol
 * hangs off exactly that offer. Excluding it is right for the OWNED view and
 * precisely wrong here: history is the thing the exchange left behind. (It is
 * not what stops the owned view double-counting either — the status sets are;
 * see the DOUBLE-COUNTING note on `bookedNolForLeg`.)
 *
 * Double counting is not a risk in this direction: a pass that survived the
 * exchange reads FULFILLED on the replacement offer, so it can never also match
 * REFUNDED here.
 *
 * Takes a LIST of leg ids, unlike its owned twin. A change-date gives the
 * passenger a NEW leg, and the pass it cancelled still names the OLD one — ask
 * only about the leg on screen and the answer is always "no pass". Callers pass
 * the leg plus the legs it superseded (see `supersededLegIds`, below).
 */
export function cancelledNolForLeg(
  booking: IBooking | undefined,
  legIds: string[]
): (INolAdded & { variant: string }) | null {
  const wanted = new Set((legIds || []).filter(Boolean));
  if (!booking || wanted.size === 0) return null;
  return scanNolForLeg(
    booking,
    wanted,
    booking.bookedOffers || [],
    CANCELLED_STATUSES,
    CANCELLED_STATUSES
  );
}

/**
 * The owned pass, shaped as the variant row the pickers render.
 *
 * `GET /bookings/{id}/fmlm-offers` answers with NOTHING for a settled booking
 * holding the pass — verified on INT booking F6PIBC (paid, upcoming, Abu
 * Dhabi→Dubai, carrying a FULFILLED `TEST4_regular` pass): `200
 * {"passengerFmLmOffers": []}`. WHICH of those two conditions drives it is not
 * established: F6PIBC has a single passenger, who is also the owner, so
 * "settled" and "already owned" are confounded. Swagger calls the endpoint
 * "offers for a PRE-BOOKED offer in a booking", which points at settled;
 * ONWARD_NOL_ADD_ENABLED existing at all points the other way. Callers must not
 * assume either — NolCardModal unions the booked riders into its checklist
 * rather than relying on the payload being wholly empty.
 *
 * Either way, every surface that draws Nol from the offers payload has nothing
 * to draw at exactly the moment the passenger opens their own pass, which is
 * what left the modal empty at AED 0 with its preselection bound to no row.
 *
 * The booking answers all of it: the variant name, the riders and what was
 * paid. `offerId` is deliberately EMPTY — this row exists to be read, not sold,
 * and callers use that emptiness to refuse a purchase they have no offer for.
 *
 * `label` is the ALREADY-STRIPPED variant, so the pickers' own nolVariantName
 * pass runs over it a second time. That is idempotent for every shape the
 * function documents; do not "fix" it on one side only.
 */
export const nolProductFromBooked = (
  booked: INolAdded,
  legId: string,
  stopRef: string,
  stopName: string
): INolProduct => ({
  type: booked.productCode,
  label: booked.variant || booked.productCode,
  // Per-passenger, to match a real offer: the modal re-multiplies by the number
  // of riders ticked, so handing it the party total would square the fare.
  // Rounded because `total` is a float-accumulated sum: 7 riders at 6.90 add up
  // to 48.3, whose quotient is 6.8999999999999995 — rendered raw on the row and
  // again in Total Fare. (Not every fixture reaches it: 3 × 20.10 accumulates a
  // tail that divides back to exactly 20.1.)
  price: booked.count ? Math.round((booked.total / booked.count) * 100) / 100 : 0,
  offerId: "",
  legId,
  tripId: "",
  stopRef,
  stopName,
  passengerRefs: booked.passengerRefs,
  offerIdByPassengerRef: {},
});

/**
 * Is this a TRAIN leg, on the BOOKING wire shape?
 *
 * `=== TRAIN`, not `!== BUS`: an unknown ptMode must not be counted toward a
 * journey's span (it would skew the endpoints and hide a pass) nor toward the
 * refund payload's leg set. Shared with `exchangedTrainDirections` so one rule
 * governs both the display and the payload halves.
 */
export const isTrainLeg = (leg: any): boolean =>
  leg?.timedLeg?.service?.mode?.ptMode === PtModeEnum.TRAIN;

/**
 * Admission states meaning "this trip was replaced" — the trip a change-date
 * moved the passenger OFF.
 *
 * NOTE the upstream gate in `GetBookingDetailDataAction` selects superseded
 * trips by `trip.direction`, which this file rejects as mistag-vulnerable. That
 * is safe only because it over-admits and this endpoint-scoped rule filters the
 * surplus back out at display — do not "align" them by loosening this one.
 *
 * Mirrors `SUPERSEDED_ADMISSION_STATUSES` in `GetBookingDetailDataAction`,
 * which does the equivalent widening one layer earlier — it is why a superseded
 * offer reaches these callers at all. Two copies; keep them in step. (The
 * pointer is restated here because this set moved out of bookingProductGroups,
 * where it originally carried it.)
 */
export const SUPERSEDED_ADMISSION_STATUSES = new Set<string>([
  AdmissionStatusEnum.EXCHANGED,
  AdmissionStatusEnum.EXCHANGE_ONGOING,
]);

/**
 * The trips `keptTrips` replaced: everything this booking exchanged away that is
 * not itself kept.
 *
 * The natural companion to `pairSupersededLegs`, which is where its result goes.
 * Exported so the two trimming actions cannot grow a fourth hand-rolled copy —
 * `GetBookingAction` being a drifted duplicate of `GetBookingDetailDataAction`
 * is what let the cancel refund bug survive as long as it did.
 *
 * Deliberately NOT direction-scoped: `pairSupersededLegs` matches on span, which
 * is the rule that filters the surplus back out. Selecting by direction here as
 * well would be redundant and would reintroduce the mistag hazard.
 *
 * `namesPassenger` is the ONE extra scope a caller may need and the span rule
 * cannot supply: booking details renders one card per passenger group, and a
 * trip exchanged away by somebody else is not that card's history. Optional
 * because only that caller has a passenger set to scope by — the cancel and
 * summary readers re-apply their own per-passenger gates downstream. It exists
 * as a parameter rather than a fourth hand-rolled copy of this filter: three
 * copies of the superseded rule are what let the cancel refund bug survive.
 */
export const supersededTripsOf = (
  allTrips: any[],
  keptTrips: any[],
  bookedOffers: any[],
  namesPassenger?: (passengerIds: string[] | undefined) => boolean
): any[] =>
  (allTrips || []).filter(
    (trip: any) =>
      !(keptTrips || []).some((kept: any) => kept?.id === trip?.id) &&
      (bookedOffers || []).some(
        (offer: any) =>
          offer?.tripCoverage?.coveredTripId === trip?.id &&
          (offer?.admissions || []).some(
            (admission: any) =>
              SUPERSEDED_ADMISSION_STATUSES.has(admission?.status) &&
              (!namesPassenger || namesPassenger(admission?.passengerIds))
          )
      )
  );

/**
 * A trip's TRAIN span, as `"<originRef>><destinationRef>"`, or `""` when it has
 * no train legs (a bus-only feeder trip) or either end is missing.
 *
 * The one rule for "are these two trips the same journey". Endpoints, NOT
 * `trip.direction`: the backend tags a return-leg trip OUT_BOUND like its
 * outbound, so a direction match makes each half of a round trip inherit the
 * other's superseded legs and a pass gets claimed for the wrong direction.
 *
 * Exported so the booking-details action can pair its superseded trips to its
 * kept ones by the SAME rule this module matches on. It scopes its own
 * supersededTrips by `trip.direction`, which deliberately over-admits on the
 * assumption that this endpoint rule filters the surplus back out at display —
 * an assumption that only holds while both sides run this function.
 *
 * Both ends come off the SAME train-filtered list, so a leg-derived origin can
 * never pair with a trip-level destination that measures a different span. A
 * trip can MIX modes (`[BUS(feeder), TRAIN]` on an FMLM journey), and bus legs
 * are never stripped from a trip upstream: `isShuttleOnlyTrip` classifies a
 * trip as a shuttle only when EVERY leg is a bus, so a mixed trip is never
 * split or reclassified and arrives here intact, bus leg included. (Trips ARE
 * dropped for other reasons — not named by `tripIds`, EXCHANGED, wrong
 * direction — none of which reshape the legs of a trip that survives.)
 * Measuring all its
 * legs would span city->DXB where the replacement spans AUH->DXB, the endpoints
 * would disagree, and the pass would go invisible.
 *
 * DO NOT let this diverge from `exchangedTrainDirections` in sessionRefundLegs,
 * which keys on stop refs for exactly the same reason — it was written with
 * `direction` first and a round-trip test caught it. Two rules, one meaning:
 * change one and change the other, or a change-date and a display disagree
 * about which journey a pass belongs to.
 *
 * BOTH ENDS are load-bearing. Fixtures that differ at both (AUH<->DXB) cannot
 * tell an origin-only rule from this one; `pairSupersededLegs.test.ts` P9 is
 * the test that does.
 */
export const trainTripEndpoints = (trip: any): string => {
  const legs = ((trip?.legs || []) as any[]).filter(isTrainLeg);
  if (legs.length === 0) return "";
  const origin = legs[0]?.timedLeg?.start?.stopPlaceRef?.stopPlaceRef || "";
  const destination =
    legs[legs.length - 1]?.timedLeg?.end?.stopPlaceRef?.stopPlaceRef || "";
  return origin && destination ? `${origin}>${destination}` : "";
};

/**
 * The legs `legTrip` REPLACED — the same-direction trips this booking has
 * exchanged away.
 *
 * A change-date issues a new leg id and the pass it cancelled goes on carrying
 * the old one, so a Nol lookup restricted to the leg on screen finds nothing.
 * Scoped to the SAME DIRECTION, so an outbound card never inherits the
 * inbound's history.
 *
 * Lives here rather than in bookingProductGroups (its first caller) because the
 * checkout card needs the same widening and importing it the other way round
 * would close an import cycle.
 *
 * KNOWN OVER-REACH: every leg of a superseded trip is returned, not just the
 * one positionally matching the leg on screen. On a CONNECTING journey a pass
 * bought against the old trip's second leg would therefore surface on the new
 * trip's FIRST-leg card, where `city` is derived from the leg on screen
 * (bookingProductGroups) and would name the wrong station. Not reachable on
 * today's network, and pre-existing in the cancelled branch this was extracted
 * from — pair the legs by index if a connecting route ever ships.
 */
export const supersededLegIds = (
  booking: IBooking | undefined,
  // `{ legId }` as well as a full `ILegTrain`: the body reads NOTHING else off
  // this argument, and the payment-summary caller works on the BOOKING trip
  // shape, which has no `ILegTrain` to hand. Widening the parameter beats
  // casting a one-field literal at the call site.
  legTrip: ILegTrain | { legId?: string } | undefined | null
): string[] => {
  if (!booking || !legTrip?.legId) return [];
  const trips = (booking.trips || []) as any[];
  const ownTrip = trips.find((trip) =>
    (trip?.legs || []).some((leg: any) => leg?.id === legTrip.legId)
  );
  if (!ownTrip) return [];
  // The span rule — endpoints, not `trip.direction` — lives in
  // `trainTripEndpoints`, which documents why. Kept in ONE place because the
  // booking-details producer pairs by the same rule, and two copies of a rule
  // that must not drift is how it drifts.
  // Booking details trims `trips` to the card's kept trips, so the superseded
  // trip is not here to be found — the handoff that action attaches names its
  // legs instead (see `cancellableLegIds`, which reads it the same way).
  const handed: string[] =
    (booking as IBooking & IDisplaySupersededLegs).displaySupersededLegIdsByTripId?.[ownTrip.id] || [];
  const ownEndpoints = trainTripEndpoints(ownTrip);
  if (!ownEndpoints) return handed;
  const derived = trips
    .filter(
      (trip) =>
        trip?.id !== ownTrip.id &&
        trainTripEndpoints(trip) === ownEndpoints &&
        (booking.bookedOffers || []).some(
          (offer: any) =>
            offer?.tripCoverage?.coveredTripId === trip?.id &&
            (offer?.admissions || []).some((a: any) =>
              SUPERSEDED_ADMISSION_STATUSES.has(a?.status || "")
            )
        )
    )
    // Train legs only: a superseded MIXED trip must not donate its bus leg id,
    // which no Nol pass names and which would only widen the match surface.
    .flatMap((trip) => ((trip?.legs || []) as any[]).filter(isTrainLeg).map((leg: any) => leg?.id))
    .filter((id: string | undefined): id is string => !!id);
  return Array.from(new Set([...derived, ...handed]));
};

/**
 * Every leg id a Nol pass for `trainLeg` could legitimately be named against:
 * the leg on screen plus the legs it superseded.
 *
 * The one place the checkout card and its hydration hook agree about scope. If
 * they disagreed, `bookedNol` and `nolAddedAtom` would disagree, and
 * `ownedNol` — which requires BOTH — would go false for a pass that exists.
 */
export const nolLegIdsFor = (
  booking: IBooking | undefined,
  // Same widening `supersededLegIds` takes, and for the same reason: the
  // tickets modal works on the BOOKING leg shape and has only an id to hand.
  // Without it every such caller re-implements this one-liner.
  trainLeg: ILegTrain | { legId?: string } | undefined | null
): string[] => [trainLeg?.legId || "", ...supersededLegIds(booking, trainLeg)].filter(Boolean);

/**
 * Pair each KEPT trip to the train legs it superseded, keyed by the kept trip's
 * id — the value `IDisplaySupersededLegs.displaySupersededLegIdsByTripId`
 * carries.
 *
 * A MAP, never a flat list. A flat list is borrowed by EVERY trip on the
 * booking: a booking row holds both directions of a round trip, and an
 * End-to-end booking's shuttle trip sits in the same set as its train. Each
 * would then match the same stranded Nol pass and the summary would add its
 * fare once per trip — AED 215 where AED 175 was due on a train+shuttle
 * booking, and the outbound's pass drawn on the inbound card of a round trip.
 *
 * Paired on `trainTripEndpoints`, NOT on `trip.direction`. Callers select their
 * superseded trips by direction, which over-admits on purpose (the backend tags
 * a return trip OUT_BOUND like its outbound); this is the rule that filters the
 * surplus back out. A bus-only feeder trip spans "" and is paired with nothing.
 *
 * Each superseded leg is claimed by AT MOST ONE kept trip, and WHICH one is
 * decided by `keptTrips` order — first matching span wins. That tie-break is
 * arbitrary rather than principled, and it is unreachable today: two kept trips
 * sharing a train span cannot occur (a connecting journey is one trip, a feeder
 * is bus-only, two exchanges deep still leaves one live trip). The guard exists
 * anyway because the whole point of this function is that a leg counted twice
 * is money charged twice — structural, not left to the shape of the data. If a
 * real booking ever produces the collision, pick the winner deliberately rather
 * than relying on order.
 *
 * Pure: no atom reads, no network. Extracted from GetBookingDetailDataAction so
 * it can be tested directly — the consumer's `?.[trip.id]` lookup is a
 * one-liner, and every rule that matters lives here.
 */
export const pairSupersededLegs = (
  keptTrips: any[],
  supersededTrips: any[]
): Record<string, string[]> => {
  const byTripId: Record<string, string[]> = {};
  const claimed = new Set<string>();
  for (const keptTrip of keptTrips || []) {
    const span = trainTripEndpoints(keptTrip);
    if (!span || !keptTrip?.id) continue;
    const legs = (supersededTrips || [])
      .filter((trip: any) => trainTripEndpoints(trip) === span)
      .flatMap((trip: any) =>
        ((trip?.legs || []) as any[]).filter(isTrainLeg).map((leg: any) => leg?.id)
      )
      .filter((id: string | undefined): id is string => !!id && !claimed.has(id));
    if (legs.length === 0) continue;
    legs.forEach((id: string) => claimed.add(id));
    byTripId[keptTrip.id] = legs;
  }
  return byTripId;
};

/**
 * The legs a cancellation covers for these trips: their own, PLUS the legs each
 * one superseded.
 *
 * A Nol pass keeps naming the leg it was bought against and Turnit never
 * re-points it after a change-date or upgrade (INT, 2026-09-03). Booking
 * details then hands every downstream reader a booking whose `trips` have been
 * narrowed to the ones its card draws, so on an exchanged booking the pass's
 * leg is on no trip here and `nolOffersForLegs` matched nothing — the train
 * refunded and the pass silently did not. Verified on QGB756: the
 * `/refund-offers` request carried the train admission and the meal ancillary
 * and no pass, so "Total refund AED 84" kept the customer's AED 40.
 *
 * Keyed per trip, never a flat union of the whole booking: the handoff pairs
 * each kept trip to its OWN superseded legs (see `pairSupersededLegs`), and
 * flattening would let one direction of a round trip refund the other's pass.
 *
 * WHO ATTACHES THE HANDOFF. Both trimming actions, at both of their write
 * sites: `GetBookingDetailDataAction` (passenger-scoped and direction/isActive)
 * and `GetBookingAction` (the `selectedTripsData` branch and the direction
 * branch). `GetBookingAction` is the one that matters most — it is the LAST
 * writer of `bookingResponseAtom` on the cancel route, running after
 * GetBookingDetailDataAction and overwriting it, so a handoff attached only
 * there never reached these readers.
 *
 * That action also had to gain a `nolOfferIds` carve-out: its offer filter is
 * inclusion-based on `tripCoverage.coveredTripId`, and a NOL offer has none, so
 * the offer was dropped from `bookedOffers` outright and no leg set could have
 * found it.
 *
 * Verified live on QGB756 (2026-09-04): `/refund-offers` went from {meal, train}
 * to {meal, train, pass 87db68fc refundable 40} and Total refund 84 -> 124.
 */
export const cancellableLegIds = (
  booking: (IBooking & IDisplaySupersededLegs) | undefined | null,
  trips: any[]
): Set<string> => {
  const ids = new Set<string>();
  const handoff = booking?.displaySupersededLegIdsByTripId || {};
  for (const trip of trips || []) {
    for (const leg of (trip?.legs || [])) {
      if (leg?.id) ids.add(leg.id);
    }
    const handed = handoff[trip?.id];
    if (handed) {
      for (const legId of handed) ids.add(legId);
      continue;
    }
    // NO HANDOFF — derive it here rather than trust that the right writer ran
    // last. `bookingResponseAtom` has ~20 writers and only the two trimming
    // actions attach the field; a raw publisher (GetShuttleBookedOffersAction's
    // mount effect, CleanupOrphanAddonsAction) can land between the trim and
    // the read. Requiring a non-wire field to survive all of them is what made
    // this bug hard to see three separate times.
    //
    // Additive, and it is worth naming exactly WHAT it can add. On a FULL
    // booking `supersededLegIds` finds the superseded trips itself,
    // endpoint-scoped so no cross-direction leak; on a trimmed booking with no
    // handoff it finds nothing and degrades to the pre-existing behaviour.
    //
    // Its exclusion is WEAKER than the handoff's: `supersededLegIds` excludes
    // only the trip being asked about, while `supersededTripsOf` excludes every
    // kept trip. So this branch can additionally donate the legs of a LIVE, kept
    // trip that happens to carry an EXCHANGED admission and share the span.
    // That needs a same-span sibling in the other direction — real inbound legs
    // run the opposite way, so it is unreachable — and the per-passenger gates
    // in both cancel readers reject anything the passenger did not select. It
    // is not "can only ever add legs" in a way that would be safe by itself.
    for (const leg of (trip?.legs || [])) {
      if (!leg?.id) continue;
      for (const legId of supersededLegIds(booking ?? undefined, { legId: leg.id })) ids.add(legId);
    }
  }
  return ids;
};

/**
 * The legs a cancellation of ONE DIRECTION covers — the single entry point for
 * both the refund QUOTE (`CancelBookingInitAction`) and the refund COMMIT
 * (`ConfirmCancelClickAction`).
 *
 * Both files carry comments insisting they must never disagree about what is
 * refunded, and that invariant has drifted twice in this area already: once on
 * the shuttle, and once when `GetBookingAction` turned out to be a drifted
 * duplicate of `GetBookingDetailDataAction` and silently dropped a stranded Nol
 * pass from the refund. Convention did not hold either time.
 *
 * So the direction filter lives HERE, with the leg widening, rather than being
 * written out identically at two call sites. Agreement is now structural: there
 * is one function, and a test can exercise the thing the actions actually call
 * instead of a hand-built copy of it.
 */
export const cancelLegsForDirection = (
  booking: (IBooking & IDisplaySupersededLegs) | undefined | null,
  direction: string
): Set<string> =>
  cancellableLegIds(
    booking,
    ((booking?.trips || []) as any[]).filter((trip: any) => trip?.direction === direction)
  );

/**
 * Non-wire fields a caller may attach to a booking for the payment summary's
 * benefit. Declared rather than reached through `as any` so a rename cannot
 * silently turn the widening into a no-op — the field is written in
 * GetBookingDetailDataAction and read in PrepareBookingSummaryAction, with no
 * type connecting them otherwise.
 *
 * `displaySupersededLegIdsByTripId` maps a KEPT trip's id to the train legs it
 * superseded. Keyed by trip because a flat list is borrowed by every trip on
 * the booking — see the note at the write site.
 */
export interface IDisplaySupersededLegs {
  displaySupersededLegIdsByTripId?: Record<string, string[]>;
  /**
   * Set ONLY by GetBookingDetailDataAction's trimmed writes: this booking is the
   * booking-details page's one-card view, re-read on that page's own load.
   * GetShuttleBookedOffersAction leaves such a view alone instead of widening it
   * to the whole booking. Deliberately not inferred from the superseded-legs map
   * above — GetBookingAction attaches that too, on the change-date, seating and
   * cancel routes, where the publish must still repair a stale snapshot.
   *
   * Lives as long as the atom does (it is persisted), until a full booking is
   * written over it. Never spread this booking into a request payload or onto
   * another surface's atom: carried to a checkout, the marker would switch off
   * the stale-snapshot repair there.
   */
  displayIsBookingDetailsView?: boolean;
}
